/**
 * Platform session model + AuthContext.
 *
 * One TanStack Query reads /api/platform/auth/me; everything else derives
 * from the cached value. RequireAuth + RequireOrgAccess wrappers gate routes.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useLocation } from "react-router-dom";
import { api, ApiError } from "@/lib/api";

export interface SessionUser {
  id: number;
  email: string;
  name: string;
  first_name: string;
  last_name: string;
  handle: string | null;
}

export interface SessionOrg {
  id: number;
  slug: string;
  name: string;
  plan: string;
  logo_url: string | null;
  is_active: boolean;
}

export interface SessionMembership {
  id: number;
  role: "owner" | "admin" | "editor" | "viewer";
  is_active: boolean;
  organization: SessionOrg;
}

export interface MeResponse {
  status: "ok";
  user: SessionUser;
  memberships: SessionMembership[];
  active_org: SessionOrg | null;
  role: SessionMembership["role"] | null;
  has_platform_access: boolean;
}

const ME_QUERY_KEY = ["platform", "auth", "me"] as const;

/** Read /me. Returns `data: undefined` while loading, `null` when unauthenticated. */
export function useSessionQuery() {
  return useQuery<MeResponse | null>({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      try {
        return await api.get<MeResponse>("/platform/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

interface SessionContextValue {
  /** undefined = loading; null = unauthenticated; MeResponse = signed in. */
  session: MeResponse | null | undefined;
  isLoading: boolean;
  /** Force-refresh /me after login / accept-invite / org-switch. */
  refresh: () => Promise<void>;
  /** Drop any cached session — used on sign-out / 401 from any endpoint. */
  clear: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useSessionQuery();

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
  }, [queryClient]);

  const clear = useCallback(() => {
    queryClient.setQueryData(ME_QUERY_KEY, null);
  }, [queryClient]);

  const value = useMemo<SessionContextValue>(
    () => ({ session: data, isLoading, refresh, clear }),
    [data, isLoading, refresh, clear],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used inside <SessionProvider>");
  }
  return ctx;
}

/** Convenience accessor — returns null while loading OR if unauthenticated. */
export function useCurrentUser(): SessionUser | null {
  const { session } = useSession();
  return session?.user ?? null;
}

/** Convenience accessor — current active organization or null. */
export function useActiveOrg(): SessionOrg | null {
  const { session } = useSession();
  return session?.active_org ?? null;
}

interface GuardProps {
  children: ReactNode;
}

/**
 * Gate any subtree on having a session AND org access.
 *  - loading: render nothing (we have the hero shader on auth pages anyway)
 *  - unauthenticated: redirect to /signin?next=<current>
 *  - authed but no org: redirect to /no-access
 */
export function RequireOrgAccess({ children }: GuardProps) {
  const { session, isLoading } = useSession();
  const location = useLocation();

  if (isLoading) return null;

  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signin?next=${next}`} replace />;
  }

  if (!session.has_platform_access) {
    return <Navigate to="/no-access" replace />;
  }

  return <>{children}</>;
}

/** For auth pages — if already signed in, bounce home. */
export function RedirectIfAuthed({ children }: GuardProps) {
  const { session, isLoading } = useSession();

  if (isLoading) return null;
  if (session?.has_platform_access) return <Navigate to="/" replace />;

  return <>{children}</>;
}
